import { useAppSelector } from "redux/hooks";
import ExternalProductLink from "components/custom/merchandise/ExternalProductLink";
import { Flex } from "../../../../styled-system/jsx";
import { TrendCell } from "views/merchandise/cells/TrendCell";
import { formatNumber } from "utils/formatNumber";
import { ProductCell } from "views/merchandise/cells/ProductCell";
import { css } from "../../../../styled-system/css";
import { type ProductQueryV4Row } from "../api/product/v4ProductReportByProduct";
import { createColumnBuilder } from "../../../components/table/columnBuilder";
import { useMemo } from "react";
import { useStandardEntityMetrics } from "views/merchandise/tables/metricUtils";

const columnHelper = createColumnBuilder<ProductQueryV4Row>();

export const useProductReportColumns = (context?: string) => {
  const channel = useAppSelector((state) => state.channel.currentChannel);

  const standardMetricIds = useStandardEntityMetrics("product");

  const columns = useMemo(
    () => [
      columnHelper.accessor("product_title", {
        header: () => <span className={css({ marginRight: "auto" })}>Product</span>,
        enableHiding: false,
        minSize: 100,
        size: 500,
        cell: (props) => {
          const { product_title } = props.row.original;

          return (
            <ProductCell
              original={{
                ...props.row.original,
                product_id: props.row.original.product_id.toString(),
                pricing_plan__id: 0,
                pricing_plan__status: props.row.original.pricing_plan_status,
                price_override_end:
                  props.row.original.price_override_end ? props.row.original.price_override_end.slice(0, 10) : "",
                cellContext: "merchandising",
              }}
              linkToNewTab={context === "productSelector"}
              value={product_title}
              showInventoryBadge={false}
              className={css({ marginRight: "auto", overflowX: "hidden" })}
            />
          );
        },
        meta: {
          columnFilterOptions: {
            label: "Product Title",
          },
        },
      }),
      columnHelper.accessor("parent_asin", {
        id: "parent_asin",
        header: "Parent ASIN",
        enableHiding: channel === "amazon",
        enableResizing: false,
        size: 110,
        cell: ({ getValue }) => {
          const asin = getValue();

          if (!asin) {
            return null;
          }

          return (
            <Flex gap={"small"} alignItems={"center"}>
              <span>{asin}</span>
              <span className={css({ paddingBottom: "xsmall" })}>
                <ExternalProductLink asin={asin} />
              </span>
            </Flex>
          );
        },
      }),
      // Invisible columns to support filtering
      columnHelper.accessor((row) => row, {
        id: "inventory_stock_state",
        header: "Inventory State",
        enableHiding: false,
        meta: {
          columnFilterOptions: {
            format: "select",
          },
        },
      }),

      columnHelper.accessor((row) => row, {
        id: "sales_state",
        header: "Sales State",
        enableHiding: false,
        meta: {
          columnFilterOptions: {
            format: "select",
          },
        },
      }),
      columnHelper.accessor((row) => row, {
        id: "tags",
        header: "Tags",
        enableHiding: false,
        meta: {
          columnFilterOptions: {
            format: "multi-select",
          },
        },
      }),
      columnHelper.accessor((row) => row, {
        id: "variations",
        header: "Variants",
        enableHiding: false,
        meta: {
          columnFilterOptions: {
            format: "select",
          },
        },
      }),
      columnHelper.accessor((row) => row, {
        id: "primary_variant",
        header: "Primary Variant",
        enableHiding: channel === "walmart",
        meta: {
          columnFilterOptions: {
            format: "select",
          },
        },
      }),
      columnHelper.accessor((row) => row, {
        id: "has_ads",
        header: "Advertising Status",
        enableHiding: false,
        meta: {
          columnFilterOptions: {
            format: "select",
          },
        },
      }),
      columnHelper.accessor("category_id", {
        id: "category_id",
        header: "Category",
        enableHiding: false,
        meta: {
          columnFilterOptions: {
            format: "select",
          },
        },
      }),
      columnHelper.accessor("pricing_plan_status", {
        id: "pricing_plan_status",
        header: "Enrollment State",
        enableHiding: false,
        meta: {
          columnFilterOptions: {
            format: "select",
          },
        },
      }),
      ...standardMetricIds.map((metricId) => {
        return columnHelper.metric(metricId);
      }),
      columnHelper.accessor("product_quantity_fba", {
        id: "product_quantity_fba",
        header: "Product Quantity FBA",
        cell: ({ getValue }) => formatNumber(getValue()),
      }),
      columnHelper.accessor("product_quantity", {
        id: "product_quantity",
        header: "Product Quantity",
        cell: ({ getValue }) => formatNumber(getValue()),
      }),
      columnHelper.accessor("trends_score", {
        id: "trends_score",
        header: "Trends",
        enableResizing: false,
        size: 75,
        cell: (props) => {
          const { margin_trend, sales_trend, trends_score } = props.row.original;

          const trends = [];

          if (margin_trend && margin_trend >= 1) {
            trends.push("Margin is trending up");
          }
          if (margin_trend && margin_trend <= -1) {
            trends.push("Margin is trending down");
          }

          if (sales_trend && sales_trend >= 1) {
            trends.push("Sales are trending up");
          }
          if (sales_trend && sales_trend <= -1) {
            trends.push("Sales are trending down");
          }

          return <TrendCell value={trends_score} trends={trends} />;
        },
      }),
    ],
    [standardMetricIds, channel, context],
  );

  return columns;
};
